/**
 * Helix integration with Shopify standard storefront events & actions.
 * - Loads the standard-events runtime (side-effect import) so Shopify.actions exists.
 * - openCart: open the Helix cart drawer (fall back to /cart when absent).
 * - updateCart: scope emitted cart events to the cart drawer.
 * - Bridges `shopify:cart:lines-update` (e.g. from an app/agent calling
 *   Shopify.actions.updateCart) into Helix's cart re-render flow.
 *
 * See: shopify.dev/docs/storefronts/themes/best-practices/standard-actions
 */
import '@shopify/events';
import { CartUpdateEvent } from '@theme/events';

function configureActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('cart-drawer-component') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.openDialog === 'function') {
        drawer.openDialog();
      } else {
        window.location.href = Theme?.routes?.cart_url || '/cart';
      }
    },
  });
}

// The runtime dispatches these events optimistically — the mutation is still in
// flight, so the cart still reports its pre-mutation state when the event fires.
// `event.promise` settles once the mutation lands; re-rendering before that paints
// stale contents. A rejected promise means the mutation failed, so leave the UI alone.
// External callers resolve with the SFAPI `{ cart, userErrors, warnings }` and never
// attach section HTML, so the item count comes from `cart.totalQuantity` and the
// section markup is re-fetched by the theme's own cart update handler.
async function settledCart(event) {
  if (!event.promise) return {};
  return (await event.promise) ?? {};
}

// When an app/agent mutates the cart via Shopify.actions.updateCart, the runtime
// emits `shopify:cart:lines-update`. Re-render Helix's cart sections by replaying
// it as Helix's internal cart update (no sections → renderSection re-fetches).
function bridgeCartLinesUpdate() {
  document.addEventListener('shopify:cart:lines-update', async (event) => {
    if (event.themeDispatched) return;

    let cart, userErrors;
    try {
      ({ cart, userErrors } = await settledCart(event));
    } catch {
      return;
    }
    // Only an add pops the drawer open, and only when the mutation fully succeeded —
    // an agent removing a line or fixing a quantity should not take over the screen.
    const autoOpenDrawer = event.action === 'add' && !userErrors?.length;
    document.dispatchEvent(
      new CartUpdateEvent({}, 'standard-action', { itemCount: cart?.totalQuantity, autoOpenDrawer })
    );
  });
}

// Discount and note mutations re-render through the same cart update path — a
// CartUpdateEvent without sections makes the cart section re-fetch, which is what
// refreshes the applied codes and the note text.
function bridgeCartMetaUpdates() {
  const replay = async (event) => {
    if (event.themeDispatched) return;

    let cart;
    try {
      ({ cart } = await settledCart(event));
    } catch {
      return;
    }
    document.dispatchEvent(
      new CartUpdateEvent({}, 'standard-action', { itemCount: cart?.totalQuantity, autoOpenDrawer: false })
    );
  };

  document.addEventListener('shopify:cart:discount-update', replay);
  document.addEventListener('shopify:cart:note-update', replay);
}

function init() {
  configureActions();
  bridgeCartLinesUpdate();
  bridgeCartMetaUpdates();
}

// Run immediately if the standard-events bundle already attached Shopify.actions;
// otherwise wait for DOMContentLoaded (fires after module scripts execute).
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
